import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, FlameKindling, Landmark } from 'lucide-react'
import { CARD_CLASS, CrystalAmount, ProgressBar, PumpkinIcon, SectionTitle } from './shared'

/**
 * Лестница наград банды: полученные отмечены, у ближайшей — полоска,
 * сгоревшие (банда была заморожена/удалялась) — приглушены.
 * nextStep — next_step банды: «тыкв хватает, нужен ещё участник».
 */
const LadderCard = ({ ladder = [], nextStep = null, minContributors }) => {
  const { t } = useTranslation()
  if (!ladder.length) return null

  return (
    <section className={`${CARD_CLASS} p-5`}>
      <SectionTitle icon={<Landmark size={18} className="text-orange-300" />}>
        {t('halloweenEvent.ladder.title')}
      </SectionTitle>
      {minContributors > 0 && (
        <p className="mt-1.5 text-xs leading-relaxed text-text-secondary">
          {t('halloweenEvent.ladder.hint', { count: minContributors })}
        </p>
      )}

      <ol className="relative mt-4 space-y-2">
        {ladder.map((item, index) => {
          const isNext = item.pct != null
          const waitsContributor = !item.done && !item.burned && nextStep?.step === item.step && nextStep?.pumpkins_done
          const isLast = index === ladder.length - 1
          const nodeClass = item.done
            ? 'border-accent-primary bg-accent-primary text-dark-primary shadow-[0_0_14px_rgba(0,255,127,0.35)]'
            : item.burned
              ? 'border-white/15 bg-white/5 text-text-secondary'
              : isNext || waitsContributor
                ? 'border-orange-400 bg-orange-500/20 text-orange-100 shadow-[0_0_14px_rgba(255,120,20,0.35)]'
                : 'border-white/15 bg-black/40 text-text-secondary'

          return (
            <li key={item.step} className="relative flex gap-3">
              {/* Узел и соединительная линия лестницы */}
              <div className="relative flex w-9 shrink-0 flex-col items-center">
                <span className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-black ${nodeClass}`}>
                  {item.done ? <Check size={16} strokeWidth={3} /> : item.burned ? <FlameKindling size={15} /> : item.step}
                </span>
                {!isLast && (
                  <span className={`absolute top-9 h-[calc(100%_-_28px)] w-0.5 ${item.done ? 'bg-accent-primary/60' : 'bg-white/10'}`} aria-hidden="true" />
                )}
              </div>

              <div
                className={`min-w-0 flex-1 rounded-xl border px-3 py-2.5 ${
                  isNext || waitsContributor
                    ? 'border-orange-400/35 bg-orange-500/[0.08]'
                    : item.done
                      ? 'border-accent-primary/20 bg-accent-primary/[0.05]'
                      : 'border-white/5 bg-black/20'
                } ${item.burned ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-sm font-bold text-white">
                    <PumpkinIcon size={16} dim={item.burned} />
                    {t('halloweenEvent.pumpkins', { count: item.pumpkins })}
                  </span>
                  <CrystalAmount
                    value={item.reward}
                    className={`text-sm font-bold ${item.burned ? 'text-text-secondary line-through' : 'text-accent-primary'}`}
                  />
                </div>
                {item.done && (
                  <p className="mt-1 text-[11px] font-semibold text-accent-primary">{t('halloweenEvent.ladder.done')}</p>
                )}
                {item.burned && (
                  <p className="mt-1 text-[11px] font-semibold text-text-secondary">{t('halloweenEvent.ladder.burned')}</p>
                )}
                {waitsContributor && (
                  <p className="mt-1 text-[11px] font-semibold text-amber-200">{t('halloweenEvent.team.needContributor')}</p>
                )}
                {isNext && (
                  <div className="mt-2 flex items-center gap-2">
                    <ProgressBar pct={item.pct} className="flex-1" />
                    <span className="w-9 shrink-0 text-right text-[11px] font-bold tabular-nums text-orange-200">{item.pct}%</span>
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </section>
  )
}

export default LadderCard
